import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Loader2 } from 'lucide-react';
import { EmbedLayout, LandingLayout } from '@/components/layout';
import { BookingFlow } from '@/components/booking';
import { useDisplayMode } from '@/hooks/useDisplayMode';
import { usePropertyContext } from '@/context/PropertyContext';
import { api, type ExperienceDetail } from '@/lib/api';
import { cn } from '@/lib/utils';
import { trackDateSelected, updateAbandonmentStep } from '@/lib/analytics';

/**
 * Booking page - multi-step booking flow for experiences
 */
export function BookPage() {
  const displayMode = useDisplayMode();
  const { propertyKey } = usePropertyContext();
  const params = useParams<{ experienceSlug: string; propertyKey?: string }>();
  const [experience, setExperience] = useState<ExperienceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const key = params.propertyKey || propertyKey;
  const slug = params.experienceSlug;

  useEffect(() => {
    async function fetchExperience() {
      if (!slug) {
        setError('Experience not found');
        setIsLoading(false);
        return;
      }

      if (!key) {
        setError('No property specified. Please access this page from a property demo.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await api.getExperience(key, slug);
        setExperience(data);

        // Track date selection step and update abandonment tracking
        if (data) {
          trackDateSelected(data.id);
          updateAbandonmentStep('date', data.id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load experience');
      } finally {
        setIsLoading(false);
      }
    }

    fetchExperience();
  }, [key, slug]);

  const Layout = displayMode === 'embed' ? EmbedLayout : LandingLayout;
  const backLink = displayMode === 'embed'
    ? `/embed/${key}/${slug}`
    : `/${slug}`;

  if (isLoading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">Loading booking details...</p>
        </div>
      </Layout>
    );
  }

  if (error || !experience) {
    const showDemoLink = !key; // Show demo link if no property key

    return (
      <Layout>
        {key && slug && (
          <Link
            to={backLink}
            className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to experience
          </Link>
        )}

        <div className="mx-auto max-w-md py-8 text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-xl font-bold">Unable to Load Booking</h2>
          <p className="mt-2 text-muted-foreground">{error || 'Experience not found'}</p>

          {showDemoLink ? (
            <Link
              to="/"
              className={cn(
                'mt-6 inline-flex items-center gap-2 rounded-lg px-6 py-3',
                'bg-primary font-medium text-primary-foreground hover:bg-primary/90'
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              View Demos
            </Link>
          ) : (
            <Link
              to={backLink}
              className={cn(
                'mt-6 inline-flex items-center gap-2 rounded-lg px-6 py-3',
                'bg-primary font-medium text-primary-foreground hover:bg-primary/90'
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </Link>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Back Link */}
      <Link
        to={backLink}
        className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to experience
      </Link>

      {/* Booking Flow */}
      <BookingFlow experience={experience} />
    </Layout>
  );
}
